import React, { useEffect, useState, useCallback, memo } from "react";
import axios from "axios";
import { getToken, toNumber, safeJSONParse } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BRANDING from "../../branding";
import CONFIG from "../../config";

const WhatsAppAdmin = memo(() => {
    // eslint-disable-next-line no-unused-vars
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language.startsWith('ar');
    const navigate = useNavigate();

    // WhatsApp Config State
    const [whatsappConfig, setWhatsappConfig] = useState(null);
    const [configForm, setConfigForm] = useState({
        phone_number_id: "",
        business_phone_number: "",
        access_token: "",
        webhook_verify_token: "",
        ai_provider: "openrouter",
        ai_model: "mistralai/mixtral-8x7b-instruct",
        ai_api_key: "",
        ai_system_prompt: "",
        auto_reply_enabled: 1,
        auto_reply_delay: 0,
        max_response_length: 1000,
        language: "auto"
    });
    const [showConfigForm, setShowConfigForm] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);

    // Templates State
    const [templates, setTemplates] = useState([]);
    const [templateForm, setTemplateForm] = useState({
        name: "",
        category: "general",
        template_text: "",
        variables: "",
        is_active: 1
    });
    const [showTemplateForm, setShowTemplateForm] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);

    // Conversations State
    const [conversations, setConversations] = useState([]);
    const [conversationStats, setConversationStats] = useState({ total: 0 });
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationFilters, setConversationFilters] = useState({
        customer_phone: "",
        is_resolved: ""
    });
    const [conversationPagination, setConversationPagination] = useState({
        limit: 50,
        offset: 0
    });

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    // Fetch WhatsApp Config
    const fetchWhatsappConfig = useCallback(async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/whatsapp/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWhatsappConfig(res.data);
            if (res.data) {
                setConfigForm({
                    phone_number_id: res.data.phone_number_id || "",
                    business_phone_number: res.data.business_phone_number || "",
                    access_token: res.data.access_token || "",
                    webhook_verify_token: res.data.webhook_verify_token || "",
                    ai_provider: res.data.ai_provider || "openrouter",
                    ai_model: res.data.ai_model || "mistralai/mixtral-8x7b-instruct",
                    ai_api_key: res.data.ai_api_key || "",
                    ai_system_prompt: res.data.ai_system_prompt || "",
                    auto_reply_enabled: res.data.auto_reply_enabled || 1,
                    auto_reply_delay: res.data.auto_reply_delay || 0,
                    max_response_length: res.data.max_response_length || 1000,
                    language: res.data.language || "auto"
                });
            }
        } catch (err) {
            console.error("Failed to fetch WhatsApp config", err);
        }
    }, []);

    // Fetch Templates
    const fetchTemplates = useCallback(async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/whatsapp/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(res.data || []);
        } catch (err) {
            console.error("Failed to fetch templates", err);
        }
    }, []);

    // Fetch Conversations
    // const fetchConversations = useCallback(async () => {
    //     setConversationLoading(true);
    //     try {
    //         const token = getToken();
    //         const params = new URLSearchParams({
    //             limit: conversationPagination.limit,
    //             offset: conversationPagination.offset,
    //             ...conversationFilters
    //         });
    //         const res = await axios.get(`${CONFIG.API_URL}/api/admin/whatsapp/conversations?${params}`, {
    //             headers: { Authorization: `Bearer ${token}` }
    //         });
    //         setConversations(res.data.conversations || []);
    //         setConversationStats({ total: res.data.total || 0 });
    //     } catch (err) {
    //         console.error("Failed to fetch conversations", err);
    //     } finally {
    //         setConversationLoading(false);
    //     }
    // }, [conversationPagination, conversationFilters]);
    const fetchConversations = useCallback(async () => {
        setConversationLoading(true);
        try {
            const token = getToken();

            const params = new URLSearchParams({
                limit: String(conversationPagination.limit),
                offset: String(conversationPagination.offset),
                ...conversationFilters
            });

            const { data } = await axios.get(
                `${CONFIG.API_URL}/api/admin/whatsapp/conversations?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setConversations(data.conversations || []);
            setConversationStats({ total: data.total || 0 });

        } catch (err) {
            console.error("Failed to fetch conversations", err);
        } finally {
            setConversationLoading(false);
        }
    }, [conversationPagination, conversationFilters]);
    useEffect(() => {
        fetchWhatsappConfig();
        fetchTemplates();
        fetchConversations();
    }, [fetchWhatsappConfig, fetchTemplates, fetchConversations]);

    // Config Handlers
    // const handleConfigChange = (e) => {
    //     const { name, value, type, checked } = e.target;
    //     setConfigForm({
    //         ...configForm,
    //         [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    //     });
    // };
    const handleConfigChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        setConfigForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (checked ? 1 : 0)
                : ["auto_reply_delay", "max_response_length"].includes(name)
                    ? toNumber(value)
                    : value
        }));
    }, []);

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setConfigLoading(true);
        setError("");
        try {
            const token = getToken();
            await axios.post(`${CONFIG.API_URL}/api/admin/whatsapp/config`, configForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage("WhatsApp configuration saved successfully");
            fetchWhatsappConfig();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration");
        } finally {
            setConfigLoading(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!window.confirm("Are you sure you want to deactivate WhatsApp integration?")) return;
        try {
            const token = getToken();
            await axios.delete(`${CONFIG.API_URL}/api/admin/whatsapp/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage("WhatsApp configuration deactivated");
            fetchWhatsappConfig();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to deactivate configuration");
        }
    };

    // Template Handlers
    // const handleTemplateChange = (e) => {
    //     const { name, value, type, checked } = e.target;
    //     setTemplateForm({
    //         ...templateForm,
    //         [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    //     });
    // };
    const handleTemplateChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        setTemplateForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value
        }));
    }, []);

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        setTemplateLoading(true);
        setError("");
        try {
            const token = getToken();
            // const payload = {
            //     ...templateForm,
            //     variables: templateForm.variables ? JSON.parse(templateForm.variables || "{}") : {}
            // };
            const payload = {
                ...templateForm,
                variables: safeJSONParse(templateForm.variables)
            };

            if (editingTemplate) {
                await axios.put(`${CONFIG.API_URL}/api/admin/whatsapp/templates/${editingTemplate.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage("Template updated successfully");
            } else {
                await axios.post(`${CONFIG.API_URL}/api/admin/whatsapp/templates`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage("Template created successfully");
            }
            setEditingTemplate(null);
            setTemplateForm({ name: "", category: "general", template_text: "", variables: "", is_active: 1 });
            setShowTemplateForm(false);
            fetchTemplates();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save template");
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setTemplateForm({
            name: template.name,
            category: template.category || "general",
            template_text: template.template_text,
            variables: template.variables ? JSON.stringify(template.variables, null, 2) : "",
            is_active: template.is_active
        });
        setShowTemplateForm(true);
    };

    const handleDeleteTemplate = async (id) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;
        try {
            const token = getToken();
            await axios.delete(`${CONFIG.API_URL}/api/admin/whatsapp/templates/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage("Template deleted successfully");
            fetchTemplates();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete template");
        }
    };

    // Conversation Handlers
    const handleResolveConversation = async (id) => {
        try {
            const token = getToken();
            await axios.patch(`${CONFIG.API_URL}/api/admin/whatsapp/conversations/${id}/resolve`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchConversations();
        } catch (err) {
            console.error("Failed to resolve conversation", err);
        }
    };

    // const handleFilterChange = (e) => {
    //     const { name, value } = e.target;
    //     setConversationFilters({
    //         ...conversationFilters,
    //         [name]: value
    //     });
    //     setConversationPagination({ ...conversationPagination, offset: 0 });
    // };
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;

        setConversationFilters(prev => ({
            ...prev,
            [name]: value
        }));

        setConversationPagination(prev => ({
            ...prev,
            offset: 0
        }));
    }, []);

    const totalPages = Math.ceil(conversationStats.total / conversationPagination.limit);

    return (
        <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            {/* Branding Header */}
            {BRANDING?.appName && (
                <div className="mb-6 pb-4 border-b">
                    <h1 className="text-3xl font-bold" style={{ color: BRANDING.colors?.primary || '#1f2937' }}>
                        {isArabic ? BRANDING.appName.ar || BRANDING.appName.en : BRANDING.appName.en}
                    </h1>
                    {BRANDING.tagline && (
                        <p className="text-gray-600 mt-1">{isArabic ? BRANDING.tagline.ar : BRANDING.tagline.en}</p>
                    )}
                </div>
            )}
            <div className="flex justify-between items-center mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.whatsapp_admin.title')}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t('admin.whatsapp_admin.subtitle')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {message && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {message}
                </div>
            )}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Configuration Section */}
            <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold">WhatsApp Business API Configuration</h2>
                    {!whatsappConfig ? (
                        <button
                            onClick={() => setShowConfigForm(!showConfigForm)}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                            {showConfigForm ? "Cancel" : "Configure WhatsApp"}
                        </button>
                    ) : (
                        <button
                            onClick={handleDeleteConfig}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                        >
                            Deactivate
                        </button>
                    )}
                </div>

                {showConfigForm && (
                    <form onSubmit={handleSaveConfig} className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Phone Number ID *</label>
                                <input
                                    type="text"
                                    name="phone_number_id"
                                    value={configForm.phone_number_id}
                                    onChange={handleConfigChange}
                                    required
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="From WhatsApp Business API"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Business Phone Number *</label>
                                <input
                                    type="text"
                                    name="business_phone_number"
                                    value={configForm.business_phone_number}
                                    onChange={handleConfigChange}
                                    required
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="+1234567890"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Access Token *</label>
                                <input
                                    type="password"
                                    name="access_token"
                                    value={configForm.access_token}
                                    onChange={handleConfigChange}
                                    required
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Webhook Verify Token</label>
                                <input
                                    type="text"
                                    name="webhook_verify_token"
                                    value={configForm.webhook_verify_token}
                                    onChange={handleConfigChange}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">AI Provider (Free)</label>
                                <select
                                    name="ai_provider"
                                    value={configForm.ai_provider}
                                    onChange={handleConfigChange}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="openrouter">OpenRouter (Free)</option>
                                    <option value="huggingface">Hugging Face (Free)</option>
                                    <option value="gemini">Google Gemini (Free)</option>
                                    <option value="openai">OpenAI (Paid)</option>
                                    <option value="anthropic">Anthropic (Paid)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">AI Model</label>
                                <input
                                    type="text"
                                    name="ai_model"
                                    value={configForm.ai_model}
                                    onChange={handleConfigChange}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="gpt-3.5-turbo"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">AI API Key</label>
                                <input
                                    type="password"
                                    name="ai_api_key"
                                    value={configForm.ai_api_key}
                                    onChange={handleConfigChange}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">AI System Prompt</label>
                                <textarea
                                    name="ai_system_prompt"
                                    value={configForm.ai_system_prompt}
                                    onChange={handleConfigChange}
                                    rows={4}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="You are a helpful customer service assistant..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Auto-Reply Enabled</label>
                                <input
                                    type="checkbox"
                                    name="auto_reply_enabled"
                                    checked={configForm.auto_reply_enabled === 1}
                                    onChange={handleConfigChange}
                                    className="h-4 w-4"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Auto-Reply Delay (seconds)</label>
                                <input
                                    type="number"
                                    name="auto_reply_delay"
                                    value={configForm.auto_reply_delay}
                                    onChange={handleConfigChange}
                                    min="0"
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Max Response Length</label>
                                <input
                                    type="number"
                                    name="max_response_length"
                                    value={configForm.max_response_length}
                                    onChange={handleConfigChange}
                                    min="1"
                                    max="4096"
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Language</label>
                                <select
                                    name="language"
                                    value={configForm.language}
                                    onChange={handleConfigChange}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="auto">Auto Detect</option>
                                    <option value="en">English</option>
                                    <option value="ar">Arabic</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="submit"
                                disabled={configLoading}
                                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                                {configLoading ? "Saving..." : "Save Configuration"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowConfigForm(false)}
                                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {whatsappConfig && !showConfigForm && (
                    <div className="p-6 bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="font-medium">Business Phone:</span>
                                <p>{whatsappConfig.business_phone_number}</p>
                            </div>
                            <div>
                                <span className="font-medium">AI Provider:</span>
                                <p>{whatsappConfig.ai_provider} ({whatsappConfig.ai_model})</p>
                            </div>
                            <div>
                                <span className="font-medium">Auto-Reply:</span>
                                <p>{whatsappConfig.auto_reply_enabled ? "Enabled" : "Disabled"}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Templates Section */}
            <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Response Templates</h2>
                    <button
                        onClick={() => {
                            setEditingTemplate(null);
                            setTemplateForm({ name: "", category: "general", template_text: "", variables: "", is_active: 1 });
                            setShowTemplateForm(!showTemplateForm);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        {showTemplateForm ? "Cancel" : "Add Template"}
                    </button>
                </div>

                {showTemplateForm && (
                    <form onSubmit={handleSaveTemplate} className="p-6 space-y-4 border-b">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Template Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={templateForm.name}
                                    onChange={handleTemplateChange}
                                    required
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Category</label>
                                <select
                                    name="category"
                                    value={templateForm.category}
                                    onChange={handleTemplateChange}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="general">General</option>
                                    <option value="product">Product Inquiry</option>
                                    <option value="price">Price Inquiry</option>
                                    <option value="delivery">Delivery Information</option>
                                    <option value="hours">Store Hours</option>
                                    <option value="greeting">Greeting</option>
                                    <option value="goodbye">Goodbye</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Template Text *</label>
                                <textarea
                                    name="template_text"
                                    value={templateForm.template_text}
                                    onChange={handleTemplateChange}
                                    required
                                    rows={4}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Hello! Thank you for contacting us. How can I help you today?"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Use {'{{variable}}'} syntax for dynamic content (e.g., {'{{name}}'}, {'{{product}}'})
                                </p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Variables (JSON)</label>
                                <textarea
                                    name="variables"
                                    value={templateForm.variables}
                                    onChange={handleTemplateChange}
                                    rows={3}
                                    className="w-full border rounded px-3 py-2 font-mono text-sm"
                                    placeholder='{"name": "string", "product": "string"}'
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Define variables that can be filled dynamically. Format: {"{ \"varName\": \"type\" }"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Active</label>
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={templateForm.is_active === 1}
                                    onChange={handleTemplateChange}
                                    className="h-4 w-4"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="submit"
                                disabled={templateLoading}
                                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                                {templateLoading ? "Saving..." : editingTemplate ? "Update Template" : "Create Template"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowTemplateForm(false);
                                    setEditingTemplate(null);
                                }}
                                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variables</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {templates.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        No templates created yet. Create your first template to help the AI provide consistent responses.
                                    </td>
                                </tr>
                            ) : (
                                templates.map((template) => (
                                    <tr key={template.id}>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium">{template.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {template.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                                            {template.template_text}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {template.variables && Object.keys(template.variables).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {Object.keys(template.variables).map((key) => (
                                                        <span key={key} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                                            {key}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">None</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {template.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() => handleEditTemplate(template)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTemplate(template.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Conversations Section */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b flex flex-wrap flex-md-auto justify-between items-center">
                    <h2 className="text-xl font-semibold">Recent Conversations</h2>
                    <div className="flex items-center space-x-4">
                        <input
                            type="text"
                            name="customer_phone"
                            value={conversationFilters.customer_phone}
                            onChange={handleFilterChange}
                            placeholder="Filter by phone..."
                            className="border rounded px-3 py-1 text-sm"
                        />
                        <select
                            name="is_resolved"
                            value={conversationFilters.is_resolved}
                            onChange={handleFilterChange}
                            className="border rounded px-3 py-1 text-sm"
                        >
                            <option value="">All Status</option>
                            <option value="0">Unresolved</option>
                            <option value="1">Resolved</option>
                        </select>
                        <button
                            onClick={fetchConversations}
                            className="bg-gray-600 text-white px-4 py-1 rounded text-sm hover:bg-gray-700"
                        >
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 text-sm">
                    <p>Total Conversations: <strong>{conversationStats.total}</strong></p>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AI Response</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {conversationLoading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center">
                                        Loading conversations...
                                    </td>
                                </tr>
                            ) : conversations.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        No conversations found.
                                    </td>
                                </tr>
                            ) : (
                                conversations.map((conv) => (
                                    <tr key={conv.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {new Date(conv.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {conv.customer_name || conv.customer_phone}
                                            <br />
                                            <span className="text-gray-500">{conv.customer_phone}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm max-w-md">
                                            <div className="truncate">{conv.message_body}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm max-w-md">
                                            {conv.ai_response ? (
                                                <div className="truncate">{conv.ai_response}</div>
                                            ) : (
                                                <span className="text-gray-400">No AI response</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${conv.is_resolved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {conv.is_resolved ? 'Resolved' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {!conv.is_resolved && (
                                                <button
                                                    onClick={() => handleResolveConversation(conv.id)}
                                                    className="text-green-600 hover:text-green-900"
                                                >
                                                    Mark Resolved
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t flex justify-between items-center">
                        <div className="text-sm text-gray-700">
                            Showing {conversationPagination.offset + 1} to{" "}
                            {Math.min(conversationPagination.offset + conversationPagination.limit, conversationStats.total)} of{" "}
                            {conversationStats.total} conversations
                        </div>
                        <div className="flex space-x-2">
                            <button
                                // onClick={() => setConversationPagination({ ...conversationPagination, offset: Math.max(0, conversationPagination.offset - conversationPagination.limit) })}
                                onClick={() =>
                                    setConversationPagination(prev => ({
                                        ...prev,
                                        offset: Math.max(0, prev.offset - prev.limit)
                                    }))
                                }
                                disabled={conversationPagination.offset === 0}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                // onClick={() => setConversationPagination({ ...conversationPagination, offset: conversationPagination.offset + conversationPagination.limit })}
                                onClick={() =>
                                    setConversationPagination(prev => ({
                                        ...prev,
                                        offset: prev.offset + prev.limit
                                    }))
                                }
                                disabled={conversationPagination.offset + conversationPagination.limit >= conversationStats.total}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Setup Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
                <h3 className="text-lg font-semibold mb-4">Setup Instructions</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>
                        <strong>Configure WhatsApp Business API:</strong> Enter your Phone Number ID, Business Phone Number, and Access Token from{" "}
                        <a href="https://developers.facebook.com/docs/whatsapp/cloud-api" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Meta WhatsApp Cloud API
                        </a>
                    </li>
                    <li>
                        <strong>Configure AI Provider (Free):</strong> Select a free AI provider (OpenRouter, Hugging Face, or Gemini) and enter your API key. Get your free API key from:
                        <ul className="list-disc list-inside ml-4 mt-1">
                            <li><a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OpenRouter.ai</a> (Free tier available)</li>
                            <li><a href="https://huggingface.co" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Hugging Face</a> (Free Inference API)</li>
                            <li><a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a> (Gemini API free)</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Create Response Templates:</strong> Add templates for common customer questions. The AI will use these as guidance for responses.
                    </li>
                    <li>
                        <strong>Set Webhook in Meta Dashboard:</strong> In your WhatsApp Business API settings, set the webhook URL to:{" "}
                        <code className="bg-white px-2 py-1 rounded text-xs">{CONFIG.API_URL}/api/whatsapp/webhook</code>
                    </li>
                    <li>
                        <strong>Test:</strong> Send a WhatsApp message to your business number and verify the auto-reply works.
                    </li>
                </ol>
            </div>
        </div>
    );
});

export default WhatsAppAdmin;

